import Checkbox from '@components/Checkbox';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import { DiscountCouponRow } from '@models/discount';
import { useFlow } from '@router/stackflow';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import useCouponControl from '@store/order/hooks/useCouponControl';
import dayjs from 'dayjs';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    coupon: DiscountCouponRow;
}

function DiscountCoupon({ coupon }:Props) {
    const { discounts, orderLines } = useCalculatedRequest();
    const { toggleDiscountCoupon } = useCouponControl();
    const {
        expiresAt,
        discountMethod,
        discountTarget,
        discountValue,
        applicableItemIds, title,
    } = coupon;
    const { push } = useFlow();
    const { t } = useTranslation('order', { keyPrefix: 'discount' });
    // 현재 이 쿠폰이 discounts 배열에 포함되어 있는지 (체크 상태)
    const isChecked = discounts
        ?.map((d) => d.couponId).includes(coupon.id);
    // 장바구니에서 이 쿠폰을 실제로 적용할 수 있는 상품 ID 목록을 계산
    const actualApplicableItemIds = useMemo(() => {
        const selectedItemIds = orderLines.map((item) => item.shopItemId);
        // 서버에서 특정 상품 ID 제한을 내려준 경우, 해당 상품 중 장바구니에 있는 것만 추림
        if (applicableItemIds && applicableItemIds.length !== 0) {
            return selectedItemIds.filter((itemId) => applicableItemIds?.includes(itemId));
        }
        // 1+1 쿠폰은 동일 상품 2개 이상 담아야 적용 가능 (1개는 무료이므로 최소 2개 필요)
        if (discountMethod === 'BOGO') {
            return selectedItemIds.filter((itemId) => {
                const { quantity } = orderLines.find((line) => line.shopItemId === itemId)!;
                return quantity >= 2;
            });
        }
        return selectedItemIds;
    }, [applicableItemIds, orderLines, discountMethod]);

    const handleSelectCoupon = () => {
        if (discountTarget === 'ITEM') {
            selectItem();
        } else {
            // 주문 전체 쿠폰(ORDER)은 상품 선택 없이 바로 적용
            toggleDiscountCoupon(coupon, isChecked || false);
        }
    };

    const selectItem = () => {
        push('DiscountCouponBottomSheet', { coupon: JSON.stringify(coupon) });
    };
    const priceUnit = usePriceUnit();
    const displayDiscountValue = useMemo(() => {
        if (discountValue) {
            if (discountMethod === 'BOGO') {
                return t('bogo');
            }
            return discountMethod === 'FIXED' ? `-${discountValue.toLocaleString()}${priceUnit}`
                : `-${discountValue}%`;
        }
        return '';
    }, [discountMethod, discountValue, t, priceUnit]);

    // 장바구니에 이 쿠폰을 적용할 수 있는 상품이 없으면 렌더링 자체를 건너뜀
    if (actualApplicableItemIds.length === 0) {
        return (<Fragment/>);
    }
    return (
        <Row fullWidth
            onClick={handleSelectCoupon}>
            <Checkbox isChecked={isChecked}
                css={css`
                          padding: 0;
                          width: 28px;
                `} />
            <Card spacing={8}>
                <Row spacing={4}>
                    <Badge >
                        {dayjs(expiresAt).format('YYYY.MM.DD')}{t('to')}
                    </Badge>
                    {discountValue && (
                        <Badge>
                            {displayDiscountValue}
                        </Badge>
                    )}
                </Row>
                <Text color={colors.gray7} size='lg'>{title}</Text>
            </Card>
        </Row>
    );
}

const Badge = styled.span`
    background-color: var(--color-gray3);
    border-radius: 6px;
    color: var(--color-gray9);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    padding: 2px 6px;
`;

const Card = styled(Column)`
    background-color: var(--color-gray1);
    border-radius: 8px;
    flex: 1;
    padding: 14px 10px 10px;
    width: 100%;
`;

export default DiscountCoupon;
